<script setup lang="ts">
/*
* Компонент UniversalFilterTree - Древовидный фильтр с поддержкой динамической загрузки
*
* Основные функции:
* - Отображение иерархических данных для фильтрации в таблицах
* - Поиск по значениям фильтра на стороне сервера с дебаунсом
* - Бесконечная прокрутка (infinite scroll) с использованием IntersectionObserver
* - Управление состоянием выбора элементов (множественный выбор)
* - Агностик к стору: принимает store через props
*/

import { VIconBtn } from 'vuetify/labs/VIconBtn'
import AnimatedLogoLoader from '@/components/createdUi/AnimatedLogoLoader.vue'
import CustomSelectedItems from '@/components/createdUi/CustomSelectedItems.vue'
import SearchHighlightText from '@/components/createdUi/SearchHighlightText.vue'

// Импорт типов
import type { IFilterChoiceItem } from '@/modules/universalStats/types/IFilterChoiceItem'
import type { IFilterStore } from '@/types/IFilterStore'
import type { IUniversalTreeSelectModule } from '@/types/IUniversalTreeSelectModule'

// Определение входных параметров компонента
const props = defineProps<{
  // Ключ поля в API, по которому осуществляется фильтрация
  fieldKey: string
  // Стор, реализующий интерфейс IFilterStore
  store: IFilterStore
  // Массив выбранных ID (v-model)
  modelValue: string[]
  // Текст подсказки в поле поиска
  placeholder?: string
  //
  module?: IUniversalTreeSelectModule
}>()

// Определение событий компонента
const emit = defineEmits<{
  // Обновление списка выбранных элементов
  (e: 'update:modelValue', value: string[]): void
  // Сигнал к применению фильтров в родительском компоненте
  (e: 'apply'): void
  // Сигнал для закрытия меню/отмены
  (e: 'cancel'): void
}>()

// Ссылка на корневую v-card для фиксации ширины при поиске
const cardRef = ref()

// Текст поискового запроса для фильтрации элементов
const search = ref('')

// Массив ID раскрытых узлов дерева
const open = ref<string[]>([])

// Ссылка на HTML-элемент для отслеживания момента достижения конца списка
const observerElement = ref<HTMLElement | null>(null)

// Экземпляр IntersectionObserver для реализации бесконечной подгрузки
let observer: IntersectionObserver | null = null

// Вычисляемый список элементов: переключается между результатами поиска и общим списком
const displayItems = computed<IFilterChoiceItem[]>(() => {
  const rawItems = search.value
    ? (props.store.searchChoices[props.fieldKey] || [])
    : (props.store.filterChoices[props.fieldKey] || [])

  // Если список пуст — выход
  if (!rawItems.length) return []

  return rawItems
})

/**
 * Загрузка следующей страницы данных фильтра при прокрутке
 */
const loadNextPage = async () => {
  const cursor = props.store.filterCursors[props.fieldKey]
  const isLoading = props.store.filterLoading[props.fieldKey]

  if (cursor && !isLoading) {
    let cursorParam: string | null
    try {
      // Парсинг значения курсора из URL или строки
      const url = new URL(cursor)
      cursorParam = url.searchParams.get('cursor')
    } catch {
      cursorParam = cursor
    }

    // Запрос данных через переданный store и module
    await props.store.fetchFilterChoices(
      props.fieldKey,
      search.value,
      cursorParam || undefined,
    )
  }
}

// Таймер для реализации задержки (debounce) при вводе в поиск
let debounceTimer: any

/**
 * Организация серверного поиска с задержкой 400мс после последнего ввода
 */
watch(search, val => {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(async () => {
    // При новом поиске вызываем метод стора с текущим module
    await props.store.fetchFilterChoices(props.fieldKey, val || '')
  }, 400)
})

/**
 * Рекурсивно собирает ID всех узлов, у которых есть children
 */
const collectAllParentIds = (items: IFilterChoiceItem[]): string[] => {
  const ids: string[] = []

  const traverse = (nodes: IFilterChoiceItem[]) => {
    for (const node of nodes) {
      if (node.children && node.children.length > 0) {
        ids.push(String(node.id))
        traverse(node.children)
      }
    }
  }

  traverse(items)
  return ids
}

// Флаг: ширина карточки уже зафиксирована
let widthLocked = false

// Следим за изменением displayItems и автоматически раскрываем все узлы с детьми
watch(displayItems, items => {
  if (items.length > 0) {
    open.value = collectAllParentIds(items)

    // После первой загрузки данных фиксируем min-width карточки,
    // чтобы ширина не прыгала при поиске
    if (!widthLocked && cardRef.value) {
      nextTick(() => {
        const el = cardRef.value?.$el
        if (el) {
          el.style.minWidth = `${el.offsetWidth}px`
          widthLocked = true
        }
      })
    }
  }
})

/**
 * Настройка IntersectionObserver при монтировании для отслеживания "маяка" в конце списка
 */
onMounted(() => {
  observer = new IntersectionObserver(async entries => {
    // Проверяем первый вход в область видимости
    if (entries[0].isIntersecting) {
      await loadNextPage()
    }
  }, {
    threshold: 0.1,
  })

  if (observerElement.value) observer.observe(observerElement.value)
})

/**
 * Очистка ресурсов при уничтожении компонента.
 * Предотвращает утечки памяти и нежелательные сетевые запросы от активных таймеров.
 */
onUnmounted(() => {
  // Отключение наблюдателя за прокруткой
  if (observer) observer.disconnect()

  // Очистка активного таймера поиска
  if (debounceTimer) {
    clearTimeout(debounceTimer)
  }
})

/**
 * Проверяет, выбран ли элемент (есть ли его ID в modelValue)
 */
const isItemSelected = (item: IFilterChoiceItem) => {
  const val = item.id
  return props.modelValue?.includes(val as any)
         || props.modelValue?.includes(String(val) as any)
         || props.modelValue?.includes(Number(val) as any)
}

const getItemValue = (item: IFilterChoiceItem): string | number => {
  return item.id
}

const getVisibleNodes = (
  nodes: IFilterChoiceItem[],
  expandedKeys: string[],
  depth = 0,
  expandChildren = false,
): any[] => {
  const visible: any[] = []

  nodes.forEach(node => {
    const isLeaf = !node.children || node.children.length === 0
    visible.push({
      ...node,
      depth,
      isLeaf,
    })

    const keyStr = String(getItemValue(node))
    const shouldShowChildren = expandedKeys.includes(keyStr) || expandChildren

    if (shouldShowChildren && node.children?.length) {
      visible.push(...getVisibleNodes(node.children, expandedKeys, depth + 1, expandChildren))
    }
  })

  return visible
}

const visibleNodes = computed(() => {
  const expandChildren = search.value.trim().length > 0
  return getVisibleNodes(displayItems.value, open.value, 0, expandChildren)
})

const toggleNode = (id: string | number) => {
  const idStr = String(id)
  const idx = open.value.indexOf(idStr)

  if (idx === -1) {
    open.value = [...open.value, idStr]
  } else {
    open.value = open.value.filter(v => v !== idStr)
  }
}

/**
 * Получение всех дочерних значений рекурсивно
 */
const getAllDescendantValues = (node: IFilterChoiceItem): any[] => {
  const vals: any[] = []
  const collect = (n: IFilterChoiceItem) => {
    if (n.children) {
      for (const child of n.children) {
        vals.push(child.id)
        collect(child)
      }
    }
  }
  collect(node)
  return vals
}

/**
 * Получение только конечных листьев рекурсивно
 */
const getLeafValues = (node: IFilterChoiceItem): any[] => {
  if (!node.children?.length) {
    return [node.id]
  }
  return node.children.flatMap(child => getLeafValues(child))
}

/**
 * Проверяет, выбран ли элемент (с учетом каскадности для родителей при leaf-стратегии)
 */
const isNodeSelected = (node: IFilterChoiceItem): boolean => {
  const strategy = props.store.filterStrategies?.[props.fieldKey] || 'independent'

  if (strategy === 'leaf') {
    if (!node.children?.length) {
      return isItemSelected(node)
    }
    const leaves = getLeafValues(node)
    return leaves.every(val =>
      props.modelValue?.includes(val as any)
      || props.modelValue?.includes(String(val) as any)
      || props.modelValue?.includes(Number(val) as any),
    )
  } else {
    return isItemSelected(node)
  }
}

/**
 * Ручная обработка выбора по клику на элемент дерева
 */
const toggleItemSelect = (item: IFilterChoiceItem) => {
  const itemValue = item.id
  const isLeaf = !item.children?.length
  const current = new Set<any>(props.modelValue || [])

  const hasSelected = (val: any) => {
    return current.has(val) || current.has(String(val)) || current.has(Number(val))
  }

  const addSelected = (val: any) => {
    current.add(val)
  }

  const deleteSelected = (val: any) => {
    current.delete(val)
    current.delete(String(val))
    current.delete(Number(val))
  }

  const strategy = props.store.filterStrategies?.[props.fieldKey] || 'independent'

  if (strategy === 'trunk') {
    // TRUNK: выбор родителя → включает всех потомков. Раскрывать и выбирать всех детей.
    const descendantValues = getAllDescendantValues(item)
    const allSelected = descendantValues.every(val => hasSelected(val)) && hasSelected(itemValue)

    if (allSelected) {
      deleteSelected(itemValue)
      descendantValues.forEach(val => deleteSelected(val))
    } else {
      addSelected(itemValue)
      descendantValues.forEach(val => addSelected(val))

      // Раскрываем этот узел и его подузлы
      if (!isLeaf) {
        const toExpand = [itemValue, ...descendantValues]
        const newOpen = new Set(open.value)
        toExpand.forEach(id => newOpen.add(String(id)))
        open.value = Array.from(newOpen)
      }
    }
  } else if (strategy === 'leaf') {
    // LEAF: выбираются только листья. Раскрывать и выбирать всех детей-листьев.
    const leafValues = getLeafValues(item)
    const allSelected = leafValues.every(val => hasSelected(val))

    if (allSelected) {
      leafValues.forEach(val => deleteSelected(val))
    } else {
      leafValues.forEach(val => addSelected(val))

      // Раскрываем этот узел и его подузлы
      if (!isLeaf) {
        const collectSubGroupIds = (node: IFilterChoiceItem): any[] => {
          if (!node.children?.length) return []
          return [node.id, ...node.children.flatMap(child => collectSubGroupIds(child))]
        }
        const toExpand = collectSubGroupIds(item)
        const newOpen = new Set(open.value)
        toExpand.forEach(id => newOpen.add(String(id)))
        open.value = Array.from(newOpen)
      }
    }
  } else {
    // INDEPENDENT / CLASSIC: НЕ раскрывать узел, а выбирать его.
    if (hasSelected(itemValue)) {
      deleteSelected(itemValue)
    } else {
      addSelected(itemValue)
    }
  }

  emit('update:modelValue', Array.from(current) as any)
}

/**
 * Плоская карта названий ID -> label для O(1) поиска выбранных элементов
 */
const labelsMap = computed(() => {
  const map = new Map<string | number, string>()
  const collect = (nodes: IFilterChoiceItem[]) => {
    for (const node of nodes) {
      map.set(getItemValue(node), node.label)
      if (node.children?.length) {
        collect(node.children)
      }
    }
  }
  const rawItems = props.store.filterChoices[props.fieldKey] || []
  collect(rawItems)
  const rawSearch = props.store.searchChoices[props.fieldKey] || []
  collect(rawSearch)
  return map
})

/**
 * Преобразование выбранных ID в объекты с именами для CustomSelectedItems
 */
const selectedItemsData = computed(() => {
  const current = props.modelValue || []
  return current.map(id => ({
    id,
    name: labelsMap.value.get(id) || labelsMap.value.get(String(id)) || labelsMap.value.get(Number(id)) || String(id),
  }))
})

const isSelectedMenuOpen = ref(false)

/**
 * Удаление элемента из списка выбранных при клике на крестик в поповере
 */
const removeItem = (id: string | number) => {
  const current = new Set<any>(props.modelValue || [])
  current.delete(id)
  current.delete(String(id))
  current.delete(Number(id))
  if (current.size === 0) {
    isSelectedMenuOpen.value = false
  }
  emit('update:modelValue', Array.from(current) as any)
}

const removeAllItems = () => {
  isSelectedMenuOpen.value = false
  emit('update:modelValue', [])
}

/**
 * Закрытие выпадающего меню Vuetify путем программной имитации Esc
 */
const handleCancel = () => {
  const event = new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', keyCode: 27, bubbles: true })
  document.dispatchEvent(event)
  emit('cancel')
}
</script>

<template>
  <VCard
    ref="cardRef"
    class="universal-filter-tree d-flex flex-column"
    min-width="300"
    max-width="500"
    height="500"
  >
    <VSheet class="pa-2 flex-grow-0">
      <VTextField
        v-model="search"
        :placeholder="placeholder || 'Поиск...'"
        variant="outlined"
        density="compact"
        :append-inner-icon="search ? undefined : 'ri-search-line'"
        hide-details
        clearable
        class="search-field mb-2"
        @click:clear="search = ''"
      />
    </VSheet>

    <!-- Оверлей выбранных элементов (при наведении) -->
    <div class="px-4 pb-2 flex-grow-0" :style="{ visibility: !props.modelValue || props.modelValue.length === 0 ? 'hidden' : 'visible' }">
      <VMenu
        v-model="isSelectedMenuOpen"
        open-on-hover
        :close-on-content-click="false"
        close-delay="200"
        location="bottom start"
        offset="8"
        content-class="date-picker-menu"
      >
        <template #activator="{ props: menuProps }">
          <span
            v-bind="menuProps"
            class="text-body-1 text-medium-emphasis cursor-pointer d-inline-block"
          >
            Выбрано: {{ props.modelValue ? props.modelValue.length : 0 }}
          </span>
        </template>

        <CustomSelectedItems
          :items="selectedItemsData"
          :title="`Выбрано ${props.modelValue ? props.modelValue.length : 0}:`"
          hide-avatar
          @remove="removeItem"
          @remove-all="removeAllItems"
        />
      </VMenu>
    </div>

    <VDivider />

    <VSheet class="overflow-y-auto flex-grow-1">
      <div class="v-list custom-treeview px-2">
        <div
          v-for="item in visibleNodes"
          :key="getItemValue(item)"
          class="v-list-item"
          :class="{
            'v-list-item--active': isNodeSelected(item),
            'tree-item--selected': isNodeSelected(item),
            'tree-item--leaf': item.isLeaf,
          }"
          :style="{ '--indent-level': item.depth }"
        >
          <span class="v-list-item__overlay" />
          <span class="v-list-item__underlay" />

          <div class="v-list-item__prepend">
            <div class="v-list-item-action v-list-item-action--start">
              <VIconBtn
                v-if="!item.isLeaf"
                icon="ri-play-fill"
                class="expand-btn"
                variant="text"
                :class="{ expanded: open.includes(String(getItemValue(item))) }"
                @click.stop="toggleNode(getItemValue(item))"
              />
              <div v-else class="node-leaf-spacer" />
            </div>
            <div class="v-list-item__spacer" />
          </div>

          <div class="v-list-item__content">
            <div class="v-list-item-title">
              <div
                class="text-body-1 text-high-emphasis py-1 text-wrap w-100 cursor-pointer"
                @click.stop="toggleItemSelect(item)"
              >
                <SearchHighlightText
                  :text="item.label"
                  :query="search"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        ref="observerElement"
        class="d-flex justify-center align-center pa-2"
        style="height: 50px; width: 100%;"
      >
        <AnimatedLogoLoader
          v-if="store.filterLoading[fieldKey]"
          :size="20"
        />
        <span v-else-if="!store.filterCursors[fieldKey] && displayItems.length > 0" class="text-caption text-grey">
          Все данные загружены
        </span>
      </div>

      <div v-if="displayItems.length === 0 && !store.filterLoading[fieldKey]" class="pa-4 text-center text-caption text-grey">
        Ничего не найдено
      </div>
    </VSheet>

    <VDivider />

    <div class="select-menu-footer">
      <VBtn
        color="secondary"
        variant="text"
        class="w-100 w-sm-auto"
        @click="handleCancel"
      >
        Отмена
      </VBtn>
      <VBtn
        color="primary"
        variant="elevated"
        class="w-100 w-sm-auto"
        @click="emit('apply')"
      >
        Применить
      </VBtn>
    </div>
  </VCard>
</template>

<style scoped lang="scss">
.universal-filter-tree {
  .text-wrap {
    white-space: normal;
    word-break: break-word;
    line-height: 20px;
    font-size: 15px;
    font-weight: 400;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  }
}

.custom-treeview {
  :deep(.v-list-item-title),
  :deep(.v-list-item-title div) {
    font-size: 15px !important;
    font-weight: 400 !important;
    line-height: 20px !important;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity)) !important;
  }

  :deep(.v-list-item--active .v-list-item-title),
  :deep(.tree-item--selected .v-list-item-title) {
    color: rgb(var(--v-theme-primary)) !important;
    font-weight: 500 !important;
  }
}

.overflow-y-auto {
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(var(--v-theme-on-surface), 0.2);
    border-radius: 4px;
  }
}
</style>
